import { Match, STATUS, MATCH_TYPE, EVENT_TYPE } from "../types/types";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Tooltip from "@mui/material/Tooltip";
import {
  dbUpdateGroupMatch,
  dbClearMatchEvents,
  dbUpdateNextRoundMatch,
} from "../api/api";
import { useAppSelector } from "../hooks";
import Edit from "@mui/icons-material/Edit";
import DoneIcon from "@mui/icons-material/Done";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useState } from "react";
import AlertDialog from "./alert";
import {
  buttonStyles,
  dialogActionsStyles,
  dialogContentStyles,
  dialogStyles,
} from "../styles/theme";
import MatchData from "./match-data";
import { getNextKnockoutMatch, getThirdPlaceMatch } from "../utils";

interface Props {
  open: boolean;
  handleClose: () => void;
  match?: Match;
}

function MatchUpdateDialog(props: Props) {
  const { open, handleClose, match } = props;
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const isAdmin = useAppSelector((state) => state.app.admin);
  const [editable, setEditable] = useState(false);

  const updateMatchStatus = async (newStatus: STATUS) => {
    if (!match?.id) return;

    await dbUpdateGroupMatch(match.id, "status", newStatus);

    // If finishing a knockout match, update the next round with the winner
    if (
      newStatus === STATUS.FINISHED &&
      match.matchType === MATCH_TYPE.KNOCKOUT_MATCH &&
      match.knockoutMatchId
    ) {
      const nextRound = getNextKnockoutMatch(match.knockoutMatchId);
      const thirdPlaceRound = getThirdPlaceMatch(match.knockoutMatchId);

      // Determine the winner and loser
      const homeGoals =
        match.homeTeamEvents?.filter((e) => e.eventType === EVENT_TYPE.GOAL)
          .length || 0;
      const awayGoals =
        match.awayTeamEvents?.filter((e) => e.eventType === EVENT_TYPE.GOAL)
          .length || 0;

      const winningTeamId =
        homeGoals > awayGoals ? match.homeTeam?.id : match.awayTeam?.id;
      const losingTeamId =
        homeGoals > awayGoals ? match.awayTeam?.id : match.homeTeam?.id;

      // Update next round with winner
      if (nextRound && winningTeamId) {
        try {
          await dbUpdateNextRoundMatch(
            nextRound.nextMatchId,
            match.pulje.id,
            winningTeamId,
            nextRound.isHomeTeam
          );
        } catch (error) {
          console.error("Failed to update next round match:", error);
        }
      }

      // Update third place match with loser (for semi-finals)
      if (thirdPlaceRound && losingTeamId) {
        try {
          await dbUpdateNextRoundMatch(
            thirdPlaceRound.nextMatchId,
            match.pulje.id,
            losingTeamId,
            thirdPlaceRound.isHomeTeam
          );
        } catch (error) {
          console.error("Failed to update third place match:", error);
        }
      }
    }

    if (newStatus === STATUS.FINISHED) handleClose();
  };

  const resetMatch = () => {
    if (!match?.id) return;

    dbClearMatchEvents(match.id);
    updateMatchStatus(STATUS.PENDING);
    setOpenAlertDialog(false);
  };

  const isKnockoutMatchTied = () => {
    if (!match || match.matchType !== MATCH_TYPE.KNOCKOUT_MATCH) return false;

    const homeGoals =
      match.homeTeamEvents?.filter((e) => e.eventType === EVENT_TYPE.GOAL)
        .length || 0;
    const awayGoals =
      match.awayTeamEvents?.filter((e) => e.eventType === EVENT_TYPE.GOAL)
        .length || 0;

    return homeGoals === awayGoals;
  };

  return (
    <div>
      <AlertDialog
        header="Er du sikker?"
        body="Er du sikker på at du vil nulstille denne kamp? Kamp-resultatet vil gå tabt."
        open={openAlertDialog}
        yesButtonText="Bekræft"
        yesCallback={resetMatch}
        noButtonText="Annuller"
        noCallback={() => setOpenAlertDialog(false)}
      />
      <Dialog
        open={open && !!isAdmin && !!match}
        onClose={handleClose}
        sx={dialogStyles}
      >
        <DialogContent sx={{ ...dialogContentStyles }}>
          {match && (
            <>
              <MatchData editable={editable} match={match} />
            </>
          )}
        </DialogContent>

        <DialogActions sx={dialogActionsStyles}>
          {match?.status !== STATUS.FINISHED && (
            <>
              <Button
                sx={buttonStyles}
                onClick={() => updateMatchStatus(STATUS.PLAYING)}
                disabled={
                  match?.status === STATUS.PLAYING ||
                  !match?.homeTeam ||
                  !match?.awayTeam
                }
              >
                Start kamp
              </Button>
              <Tooltip
                title={
                  isKnockoutMatchTied()
                    ? "Knock-out kampe kan ikke slutte uafgjort"
                    : ""
                }
                arrow
              >
                <span>
                  <Button
                    sx={buttonStyles}
                    onClick={() => updateMatchStatus(STATUS.FINISHED)}
                    disabled={
                      match?.status !== STATUS.PLAYING || isKnockoutMatchTied()
                    }
                  >
                    Afslut kamp
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {match?.status === STATUS.FINISHED && (
            <>
              {!editable && (
                <Button
                  sx={buttonStyles}
                  onClick={() => setOpenAlertDialog(true)}
                >
                  <RestartAltIcon />
                </Button>
              )}
              {editable ? (
                <Button sx={buttonStyles} onClick={() => setEditable(false)}>
                  <DoneIcon />
                </Button>
              ) : (
                <Button sx={buttonStyles} onClick={() => setEditable(true)}>
                  <Edit />
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default MatchUpdateDialog;
